// =============================================================================
// SIREPPAR - Wizard Candidatura, Paso 2: Domicilio y Residencia
// =============================================================================

import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { candidatoService } from '../../../services/api';
import type { Candidato } from './CandidaturaWizardPage';

interface Props {
  candidato: Candidato;
  onSaved: () => void;
  onBack: () => void;
  onNext: () => void;
}

const MINIMO_ANIOS_RESIDENCIA = 10; // Art. 161 Carta Orgánica Municipal de Gaiman

export default function Paso2Domicilio({ candidato, onSaved, onBack, onNext }: Props) {
  const [form, setForm] = useState({
    domicilioCalle: candidato.domicilioCalle || '',
    domicilioNumero: candidato.domicilioNumero || '',
    domicilioPisoDepto: candidato.domicilioPisoDepto || '',
    localidad: candidato.localidad || 'Gaiman',
    codigoPostal: candidato.codigoPostal || '9105',
    anosResidenciaDeclarada: candidato.anosResidenciaDeclarada?.toString() || '',
    mesesResidenciaDeclarada: candidato.mesesResidenciaDeclarada?.toString() || '0',
    observaciones: candidato.observaciones || '',
  });
  const [datosPadron, setDatosPadron] = useState<{ circuito: string | null; localidad: string | null } | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const montado = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const comprobantes = candidato.documentos.filter(d => d.tipo === 'COMPROBANTE_RESIDENCIA');

  // Circuito electoral: solo informativo, se consulta contra el padrón pero no se guarda
  useEffect(() => {
    if (!candidato.dni) return;
    candidatoService.verificarDni(candidato.dni)
      .then(res => setDatosPadron({ circuito: res.data.data?.circuito || null, localidad: res.data.data?.localidad || null }))
      .catch(() => setDatosPadron(null));
  }, [candidato.dni]);

  const circuitoTexto = datosPadron?.circuito
    ? `${datosPadron.circuito}${datosPadron.localidad ? ` — ${datosPadron.localidad}` : ''}`
    : 'Sin datos del padrón';

  const guardarAhora = async (avisar = false) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setGuardando(true);
    try {
      await candidatoService.actualizar(candidato.id, {
        ...form,
        anosResidenciaDeclarada: form.anosResidenciaDeclarada ? parseInt(form.anosResidenciaDeclarada, 10) : null,
        mesesResidenciaDeclarada: parseInt(form.mesesResidenciaDeclarada, 10) || 0,
      });
      onSaved();
      if (avisar) toast.success('Borrador guardado');
    } catch {
      toast.error('No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  // Autosave con debounce de 800ms
  useEffect(() => {
    if (!montado.current) {
      montado.current = true;
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { guardarAhora(); }, 800);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const subirComprobante = async (archivo: File) => {
    setSubiendo(true);
    try {
      await candidatoService.subirDocumento(candidato.id, 'COMPROBANTE_RESIDENCIA', archivo);
      toast.success('Comprobante subido');
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo subir el archivo');
    } finally {
      setSubiendo(false);
    }
  };

  const eliminarComprobante = async (documentoId: string) => {
    try {
      await candidatoService.eliminarDocumento(documentoId);
      toast.success('Comprobante eliminado');
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo eliminar el documento');
    }
  };

  const anios = form.anosResidenciaDeclarada ? parseInt(form.anosResidenciaDeclarada, 10) : null;
  const residenciaInsuficiente = anios !== null && anios < MINIMO_ANIOS_RESIDENCIA;

  const puedeContinuar =
    form.domicilioCalle.trim() && form.domicilioNumero.trim() && form.localidad.trim() &&
    form.codigoPostal.trim() && anios !== null && comprobantes.length > 0;

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
        <strong>Requisito de residencia:</strong> la Carta Orgánica de Gaiman exige una residencia mínima de <strong>10 años continuos, inmediatos y comprobables</strong> en el Municipio (Art. 161). Se acredita por cualquier medio de prueba excepto testimonial.
      </div>

      <div className="bg-white rounded-xl shadow p-6 space-y-4">
        <h3 className="font-semibold text-gray-900">Domicilio electoral actual</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Calle *</label>
            <input type="text" value={form.domicilioCalle}
              onChange={e => setForm(f => ({ ...f, domicilioCalle: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Número *</label>
            <input type="text" value={form.domicilioNumero}
              onChange={e => setForm(f => ({ ...f, domicilioNumero: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Piso / Depto (opcional)</label>
          <input type="text" value={form.domicilioPisoDepto}
            onChange={e => setForm(f => ({ ...f, domicilioPisoDepto: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Localidad *</label>
            <input type="text" value={form.localidad}
              onChange={e => setForm(f => ({ ...f, localidad: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Código Postal *</label>
            <input type="text" value={form.codigoPostal}
              onChange={e => setForm(f => ({ ...f, codigoPostal: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
          <input type="text" value="Chubut" readOnly className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-gray-500" />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6 space-y-4">
        <h3 className="font-semibold text-gray-900">Acreditación de residencia</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Antigüedad de residencia declarada *</label>
          <div className="flex gap-3 items-center">
            <div>
              <input type="number" min={0} value={form.anosResidenciaDeclarada}
                onChange={e => setForm(f => ({ ...f, anosResidenciaDeclarada: e.target.value }))}
                className="w-full md:w-28 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <span className="text-xs text-gray-500 block mt-0.5">años</span>
            </div>
            <div>
              <select value={form.mesesResidenciaDeclarada}
                onChange={e => setForm(f => ({ ...f, mesesResidenciaDeclarada: e.target.value }))}
                className="w-full md:w-28 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                {Array.from({ length: 12 }, (_, i) => i).map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <span className="text-xs text-gray-500 block mt-0.5">meses</span>
            </div>
          </div>
          {residenciaInsuficiente && (
            <p className="text-xs text-red-700 mt-1 font-medium">⚠ Por debajo del mínimo exigido por la Carta Orgánica de Gaiman (10 años). Se puede continuar cargando, pero va a ser observado en el análisis.</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Medios probatorios adjuntos *</label>
          <p className="text-xs text-gray-500 mb-2">Ejemplos válidos: certificado de domicilio policial, facturas de servicios públicos a tu nombre, contrato de alquiler, o constancia de la Municipalidad de Gaiman.</p>
          <div className="space-y-2">
            {comprobantes.map(doc => (
              <div key={doc.id} className="border border-green-200 bg-green-50 rounded-lg p-3 text-sm text-green-800 flex items-center justify-between gap-2">
                <span className="flex-1 truncate">📄 {doc.nombreOriginal} · {(doc.tamanioBytes / 1024).toFixed(0)} KB</span>
                <button type="button" onClick={() => eliminarComprobante(doc.id)} className="text-red-500 hover:text-red-700 flex-shrink-0" title="Eliminar">✕</button>
              </div>
            ))}
            <label className="border border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center text-gray-500 text-sm cursor-pointer hover:border-blue-400 hover:bg-blue-50">
              {subiendo ? 'Subiendo...' : '+ Agregar otro medio probatorio'}
              <span className="text-xs text-gray-400 mt-1">PDF, JPG o PNG · Máx 5MB</span>
              <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" disabled={subiendo}
                onChange={e => e.target.files?.[0] && subirComprobante(e.target.files[0])} />
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones adicionales (opcional)</label>
          <textarea rows={3} value={form.observaciones}
            onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">← Volver</button>
        <button onClick={() => guardarAhora(true)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">
          {guardando ? 'Guardando...' : 'Guardar borrador'}
        </button>
        <button
          onClick={async () => { await guardarAhora(); onNext(); }}
          disabled={!puedeContinuar}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium"
        >
          Continuar a foto y boleta →
        </button>
      </div>
    </div>
  );
}
